/** /support-tickets — support tickets clients' AI escalated over MCP (client MCP plan §5.4.1, Q5;
 *  WPS FE-03, backend P1-11).
 *
 *  One row per ticket with our facts only — the title is our most important finding, never the
 *  client's words (those are on the ticket's page, marked untrusted). `Announced` says per sink
 *  how its deliveries went (the opening and each follow-up is one delivery): a failed one is an
 *  announcement support never received. A row opens the ticket.
 *
 *  Filters live in the URL (linkable, preset-covered): tenant (console scope aware), status,
 *  severity, delivery status and the opened window; the URL-only `booking` lists the tickets whose
 *  case covers one booking. Gated by `console_support_tickets_view` (Ops Admin by default). */
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Link,
  ListItemText,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { getGridSingleSelectOperators, type GridColDef } from '@mui/x-data-grid-pro';
import { useMemo, type ReactNode } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import * as ticketsApi from '@/api/supportTickets';
import {
  DELIVERY_STATUSES,
  DIAGNOSE_STATE_LABELS,
  severityColor,
  severityLabel,
  statusColor,
  statusLabel,
  SUBJECT_SCOPE_LABELS,
  supportTicketKeys,
  TICKET_SEVERITIES,
  TICKET_STATUSES,
  type SupportTicket,
} from '@/api/supportTickets';
import { copyableColumn } from '@/components/DataGrid/copyableColumn';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import {
  ResourceDataGrid,
  type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { ResponsiveFilterBar, type FilterBarItem } from '@/components/DataGrid/ResponsiveFilterBar';
import { PageHeader } from '@/components/PageHeader';
import { WithTenantMark } from '@/components/TenantMark/TenantMark';
import { TenantPicker } from '@/components/TenantPicker/TenantPicker';
import { boundToParam, paramToBound, TimeWindowPicker } from '@/components/TimeWindowPicker';
import { operatorActions } from '@/components/ValueActions/identity';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { useGridFilters, type FilterDef } from '@/hooks/useGridFilters';
import { makeResourceFiltersAdapter, useGridPresets } from '@/hooks/useGridPresets';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { pageTenantsFrom } from '@/scope/pageTenants';
import { formatTenantIds, tenantIdsToApi } from '@/scope/tenantIds';
import { useTenantScopeList } from '@/scope/tenantScope';
import { formatDate, formatRelativeTime } from '@/utils/format';

import { SinkChips } from './SinkChips';
import {
  bookingActions,
  severityActions,
  statusActions,
  supportTicketHref,
  tenantGrid,
  ticketActions,
} from './supportTicketLinks';

const digits = (v: string) => (/^\d+$/.test(v) ? Number(v) : undefined);
/** An ISO instant or a relative token (`now-7d`) — the backend reads both. */
const isoOrDrop = (v: string) => (paramToBound(v) ? v : undefined);

/** A comma list kept to the known values — an unknown one would 400 the whole grid. */
const knownList =
  (known: readonly { value: string }[]) =>
  (v: string): string | undefined => {
    const values = v.split(',').filter((x) => known.some((k) => k.value === x));
    return values.length ? values.join(',') : undefined;
  };

const FILTERS: FilterDef[] = [
  { param: 'tenant', apiParam: 'tour_operator_id', toApi: tenantIdsToApi, tenantScope: 'multi' },
  {
    param: 'status',
    toApi: knownList(TICKET_STATUSES),
    column: { field: 'status', operator: 'is' },
  },
  {
    param: 'severity',
    toApi: knownList(TICKET_SEVERITIES),
    column: { field: 'severity', operator: 'is' },
  },
  {
    param: 'delivery_status',
    toApi: knownList(DELIVERY_STATUSES),
    column: { field: 'sinks', operator: 'is' },
  },
  { param: 'from', apiParam: 'created_after', toApi: isoOrDrop },
  { param: 'to', apiParam: 'created_before', toApi: isoOrDrop },
  // URL-only: "Tickets covering this booking" from a ticket's or a booking's page.
  { param: 'booking', apiParam: 'booking_id', toApi: digits },
];
const PRESET_FILTERS = makeResourceFiltersAdapter(FILTERS);

/** Column order (mirrors the columns memo — keep in sync). */
const FIELD_ORDER = [
  'public_id',
  'created_ts',
  'tenant',
  'severity',
  'status',
  'title',
  'subject',
  'escalation_count',
  'last_escalated_ts',
  'sinks',
  'diagnose_state',
  'finding_code',
  'status_changed_ts',
  'updated_ts',
  'id',
];
const DEFAULT_HIDDEN = ['finding_code', 'status_changed_ts', 'updated_ts', 'id'];

const isOperator = getGridSingleSelectOperators().filter((op) => op.value === 'is');

/** A grid cell link that navigates by itself (cmd-click opens a tab) instead of the row. */
function CellLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link component={RouterLink} to={to} underline="hover" onClick={(e) => e.stopPropagation()}>
      {children}
    </Link>
  );
}

function When({ iso }: { iso: string | null }) {
  if (!iso) return <>—</>;
  return (
    <Tooltip title={formatDate(iso)} describeChild>
      <span>{formatRelativeTime(iso)}</span>
    </Tooltip>
  );
}

/** The subject: its scope and the booking the case was opened on, plus how many more it covers. */
function SubjectCell({ subject }: { subject: SupportTicket['subject'] }) {
  const scope = SUBJECT_SCOPE_LABELS[subject.scope];
  const more = subject.booking_count - 1;
  return (
    <span>
      {scope}
      {subject.booking_id !== null && (
        <>
          {' · '}
          <ValueActions actions={bookingActions(subject.booking_id)}>
            <CellLink to={`/bookings/${subject.booking_id}`}>#{subject.booking_id}</CellLink>
          </ValueActions>
        </>
      )}
      {more > 0 && (
        <Typography variant="body2" component="span" color="text.secondary">
          {` +${more}${subject.truncated ? '…' : ''}`}
        </Typography>
      )}
    </span>
  );
}

function MultiSelect<V extends string>({
  label,
  options,
  values,
  width,
  onChange,
}: {
  label: string;
  options: readonly { value: V; label: string }[];
  values: string[];
  width: number;
  onChange: (next: string | null) => void;
}) {
  const labelOf = (v: string) => options.find((o) => o.value === v)?.label ?? v;
  return (
    <TextField
      select
      size="small"
      label={label}
      value={values}
      onChange={(e) => onChange((e.target.value as unknown as string[]).join(',') || null)}
      sx={{ width }}
      SelectProps={{
        multiple: true,
        renderValue: (sel) => (sel as string[]).map(labelOf).join(', '),
      }}
    >
      {options.map((o) => (
        <MenuItem key={o.value} value={o.value}>
          <Checkbox size="small" checked={values.includes(o.value)} sx={{ py: 0 }} />
          <ListItemText primary={o.label} />
        </MenuItem>
      ))}
    </TextField>
  );
}

export function SupportTicketsPage() {
  const navigate = useNavigate();

  const presets = useGridPresets({
    grid: 'support_tickets',
    defaultOrder: FIELD_ORDER,
    defaultHidden: DEFAULT_HIDDEN,
    defaultOrdering: '-created_ts',
    filters: PRESET_FILTERS,
  });
  const {
    filterModel,
    onFilterModelChange,
    extra,
    getParam,
    setParam,
    setParams,
    resetAll: resetAllFilters,
    anyActive: anyFilterActive,
  } = useGridFilters(FILTERS, presets.filtersLayer);

  // The operators the effective tenant filter names (URL > preset > scope).
  const pageTenants = pageTenantsFrom(getParam('tenant'), useTenantScopeList());
  const listOf = (param: string) => (getParam(param) ?? '').split(',').filter(Boolean);
  const statuses = listOf('status');
  const severities = listOf('severity');
  const deliveries = listOf('delivery_status');
  const from = getParam('from');
  const to = getParam('to');
  const bookingRaw = getParam('booking');
  const booking = bookingRaw && /^\d+$/.test(bookingRaw) ? Number(bookingRaw) : null;

  const columns = useMemo<GridColDef<SupportTicket>[]>(
    () => [
      {
        field: 'public_id',
        headerName: 'Ticket',
        width: 140,
        filterable: false,
        renderCell: (p) => (
          <ValueActions actions={ticketActions(p.row.public_id)}>
            <CellLink to={supportTicketHref(p.row.public_id)}>
              <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 13 }}>
                {p.row.public_id}
              </Box>
            </CellLink>
          </ValueActions>
        ),
      },
      {
        field: 'created_ts',
        headerName: 'Opened',
        width: 140,
        filterable: false,
        renderCell: (p) => <When iso={p.row.created_ts} />,
      },
      {
        field: 'tenant',
        headerName: 'Tenant',
        width: 150,
        sortable: false,
        filterable: false,
        valueGetter: (_, row) => row.tenant?.code ?? `#${row.tour_operator_id}`,
        renderCell: (p) =>
          p.row.tenant ? (
            <WithTenantMark environment={p.row.tenant.environment}>
              <ValueActions actions={operatorActions(p.row.tenant, tenantGrid(p.row.tenant.id))}>
                {p.row.tenant.code || p.row.tenant.name}
              </ValueActions>
            </WithTenantMark>
          ) : (
            `#${p.row.tour_operator_id}`
          ),
      },
      {
        field: 'severity',
        headerName: 'Severity',
        width: 120,
        type: 'singleSelect',
        valueOptions: TICKET_SEVERITIES.map((s) => ({ value: s.value, label: s.label })),
        filterOperators: isOperator,
        renderCell: (p) => (
          <ValueActions chip actions={severityActions(p.row.severity)}>
            <Chip
              size="small"
              variant="outlined"
              color={severityColor(p.row.severity)}
              label={severityLabel(p.row.severity)}
            />
          </ValueActions>
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 140,
        type: 'singleSelect',
        valueOptions: TICKET_STATUSES.map((s) => ({ value: s.value, label: s.label })),
        filterOperators: isOperator,
        renderCell: (p) => (
          <ValueActions chip actions={statusActions(p.row.status)}>
            <Chip
              size="small"
              color={statusColor(p.row.status)}
              label={statusLabel(p.row.status)}
            />
          </ValueActions>
        ),
      },
      {
        field: 'title',
        headerName: 'What we found',
        flex: 1,
        minWidth: 240,
        sortable: false,
        filterable: false,
      },
      {
        field: 'subject',
        headerName: 'Subject',
        width: 170,
        sortable: false,
        filterable: false,
        valueGetter: (_, row) =>
          `${SUBJECT_SCOPE_LABELS[row.subject.scope]} ${row.subject.booking_id ?? ''}`.trim(),
        renderCell: (p) => <SubjectCell subject={p.row.subject} />,
      },
      {
        field: 'escalation_count',
        headerName: 'Escalations',
        width: 110,
        type: 'number',
        filterable: false,
      },
      {
        field: 'last_escalated_ts',
        headerName: 'Last escalated',
        width: 140,
        filterable: false,
        renderCell: (p) => <When iso={p.row.last_escalated_ts} />,
      },
      {
        field: 'sinks',
        headerName: 'Announced',
        width: 170,
        sortable: false,
        type: 'singleSelect',
        valueOptions: DELIVERY_STATUSES.map((s) => ({ value: s.value, label: s.label })),
        filterOperators: isOperator,
        valueGetter: (_, row) => row.sinks.map((s) => `${s.sink}: ${s.status}`).join(', '),
        renderCell: (p) => <SinkChips sinks={p.row.sinks} />,
      },
      {
        field: 'diagnose_state',
        headerName: 'Diagnose',
        width: 110,
        sortable: false,
        filterable: false,
        valueGetter: (_, row) => DIAGNOSE_STATE_LABELS[row.diagnose_state],
      },
      {
        field: 'finding_code',
        headerName: 'Finding code',
        width: 200,
        sortable: false,
        filterable: false,
      },
      {
        field: 'status_changed_ts',
        headerName: 'Status changed',
        width: 140,
        filterable: false,
        renderCell: (p) => <When iso={p.row.status_changed_ts} />,
      },
      {
        field: 'updated_ts',
        headerName: 'Updated',
        width: 140,
        filterable: false,
        renderCell: (p) => <When iso={p.row.updated_ts} />,
      },
      copyableColumn({ field: 'id', headerName: 'ID', width: 90, filterable: false }),
    ],
    [],
  );

  const queryFn = (params: ResourceDataGridParams) =>
    ticketsApi.list({
      ...(params.extra as ticketsApi.SupportTicketFilters),
      ...(params.search ? { search: params.search } : {}),
      ordering: params.ordering ?? presets.ordering,
      ...(params.timeoutTier ? { timeout: params.timeoutTier } : {}),
      page: params.page,
      page_size: params.pageSize,
    });

  const toolbarItems: FilterBarItem[] = [
    {
      key: 'when',
      priority: 6,
      active: Boolean(from || to),
      node: (
        <TimeWindowPicker
          label="Opened"
          allowOpenEnded
          value={[paramToBound(from), paramToBound(to, { end: true })]}
          onChange={([start, end]) =>
            setParams({ from: boundToParam(start), to: boundToParam(end) })
          }
        />
      ),
    },
    {
      key: 'tenant',
      priority: 5,
      active: pageTenants.length > 0,
      node: (
        <Box sx={{ minWidth: 260 }}>
          <TenantPicker
            multiple
            value={pageTenants}
            onChange={(next) => setParam('tenant', formatTenantIds(next.map((t) => t.id)))}
          />
        </Box>
      ),
    },
    {
      key: 'status',
      priority: 4,
      active: statuses.length > 0,
      node: (
        <MultiSelect
          label="Status"
          options={TICKET_STATUSES}
          values={statuses}
          width={190}
          onChange={(v) => setParam('status', v)}
        />
      ),
    },
    {
      key: 'severity',
      priority: 3,
      active: severities.length > 0,
      node: (
        <MultiSelect
          label="Severity"
          options={TICKET_SEVERITIES}
          values={severities}
          width={160}
          onChange={(v) => setParam('severity', v)}
        />
      ),
    },
    {
      key: 'delivery',
      priority: 2,
      active: deliveries.length > 0,
      node: (
        <MultiSelect
          label="Announcement"
          options={DELIVERY_STATUSES}
          values={deliveries}
          width={170}
          onChange={(v) => setParam('delivery_status', v)}
        />
      ),
    },
  ];

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="support-tickets"
        title="Support tickets"
        subtitle="Cases clients’ AI escalated to us over MCP — what our checks found, who was told, and where each ticket stands."
      />

      <ResponsiveFilterBar items={toolbarItems} />

      {booking !== null && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setParam('booking', null)}>
          Tickets whose case covers booking{' '}
          <Link component={RouterLink} to={`/bookings/${booking}`} underline="hover">
            #{booking}
          </Link>
        </Alert>
      )}

      {presets.blocking ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <ResourceDataGrid<SupportTicket>
          queryKey={supportTicketKeys.list}
          queryFn={queryFn}
          allowExtendedTimeout
          resetPageOnQueryChange
          columns={orderColumns(columns, presets.orderedFields)}
          defaultSort={{ field: 'created_ts', sort: 'desc' }}
          extra={extra}
          filterModel={filterModel}
          onFilterModelChange={onFilterModelChange}
          sortModel={presets.sortModel}
          onSortModelChange={presets.onSortModelChange}
          columnVisibilityModel={presets.columnVisibilityModel}
          onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
          onColumnOrderChange={presets.onColumnOrderChange}
          onRowClick={(_id, row) => navigate(supportTicketHref(row.public_id))}
          emptyMessage="No support tickets yet. They appear here when a client’s AI escalates a case over MCP."
          headerToolbar={{
            onResetAllFilters: resetAllFilters,
            resetDisabled: !anyFilterActive,
            search: true,
            searchPlaceholder: 'Ticket id (TO-…) or title',
            exportFileName: 'support-tickets',
            presets: presets.toolbar,
          }}
        />
      )}
    </Box>
  );
}
